import React from 'react';
import type {
  CashflowPredictionSnapshot,
  ForecastBranchBalance,
  RiskThresholdConfig,
} from '../types';
import {
  formatAmountCurrency,
  formatDisplayDate,
  getRiskCoverageDescription,
  getRiskPointerPercent,
  getRiskSegment,
} from '../utils';

interface SummaryCardsProps {
  branchBalances: ForecastBranchBalance[];
  prediction?: CashflowPredictionSnapshot;
  riskConfig: RiskThresholdConfig;
}

const SummaryCards: React.FC<SummaryCardsProps> = ({
  branchBalances,
  prediction,
  riskConfig,
}) => {
  const riskMonths = prediction?.riskCoverageMonths ?? 0;
  const riskSegment = getRiskSegment(riskConfig, riskMonths);
  const riskReferenceDateText = prediction
    ? formatDisplayDate(prediction.riskReferenceDate)
    : '';
  const shouldShowRiskCoverageStar =
    !!prediction &&
    prediction.riskAvailableDays < 720 &&
    !!riskReferenceDateText;
  const pointerPercent = getRiskPointerPercent(
    riskConfig,
    (prediction?.riskAvailableDays ?? 0) / 30
  );
  const displayBranchBalances = prediction?.branchBalances || branchBalances;

  return (
    <section className="grid grid-cols-1 gap-3 xl:grid-cols-2">
      <article className="min-h-[140px] rounded-[9px] border border-solid border-[#e5e7eb] bg-white px-[18px] py-[14px] shadow-[0_1px_4px_rgba(15,23,42,0.05)]">
        <div className="mb-[10px] flex items-center gap-[5px] text-[12.5px] font-medium text-[#6b7280]">
          <span aria-hidden="true">💰</span>
          <span>当前账户余额</span>
        </div>
        <div className="text-[24px] font-semibold leading-[1.15] text-[#0f172a]">
          {prediction
            ? formatAmountCurrency(prediction.currentBalanceWan * 10000)
            : '¥ ——'}
          <span className="ml-[6px] align-[2px] text-[10.5px] font-normal text-[#94a3b8]">
            {prediction
              ? `更新于 ${formatDisplayDate(prediction.currentBalanceUpdatedAt || prediction.rangeStart)}`
              : '待预测'}
          </span>
        </div>
        <div className="mt-[6px] text-[10.5px] leading-[1.55] text-[#475569]">
          {displayBranchBalances.map(branch => (
            <div key={branch.id} className="flex items-center justify-between">
              <div className="text-[#6b7280]">{branch.name}</div>
              <div className="inline-flex items-baseline gap-2 text-right">
                <div className="text-[12.5px] font-medium text-[#111827]">
                  {prediction
                    ? formatAmountCurrency(branch.amountWan * 10000)
                    : '¥ ——'}
                </div>
                <div className="text-[10px] font-normal text-[#94a3b8]">
                  {prediction
                    ? `更新于 ${formatDisplayDate(branch.updatedAt, 'YYYY-MM-DD HH:mm')}`
                    : '——'}
                </div>
              </div>
            </div>
          ))}
        </div>
      </article>

      <article className="min-h-[140px] rounded-[9px] border border-solid border-[#e5e7eb] bg-white px-[18px] py-[14px] shadow-[0_1px_4px_rgba(15,23,42,0.05)]">
        <div className="mb-[10px] flex items-center gap-[5px] text-[12.5px] font-medium text-[#6b7280]">
          <span aria-hidden="true">🛡️</span>
          <span>当前风险等级</span>
        </div>
        <div
          className="text-[18px] font-semibold leading-[1.15]"
          style={{ color: prediction ? riskSegment.textColor : '#cbd5e1' }}
        >
          {prediction ? prediction.riskLevelLabel || riskSegment.label : '——'}
        </div>
        <div className="mt-1 text-[11px] leading-[1.45] text-[#6b7280]">
          {prediction ? (
            <>
              {getRiskCoverageDescription(
                prediction.riskAvailableDays,
                prediction.riskReferenceDate
              )}
              {shouldShowRiskCoverageStar ? (
                <span className="ml-1 text-[#f59e0b]">★</span>
              ) : null}
            </>
          ) : (
            '待预测：可覆盖月数 / 覆盖期终点'
          )}
        </div>

        <div className="mt-2">
          <div className="relative pt-4">
            <div
              className="absolute top-0 text-[13px] leading-none transition-all"
              style={{
                left: prediction ? `${pointerPercent}%` : '50%',
                transform: 'translateX(-50%)',
                color: prediction ? riskSegment.textColor : '#cbd5e1',
                visibility: prediction ? 'visible' : 'hidden',
              }}
            >
              ▼
            </div>
            <div className="overflow-hidden rounded-[3.5px] shadow-[inset_0_1px_2px_rgba(0,0,0,0.05)]">
              <div className="grid h-[7px] grid-cols-4">
                {riskConfig.segments.map(segment => (
                  <div
                    key={segment.key}
                    className="h-full"
                    style={{
                      backgroundColor: segment.color,
                      opacity: prediction ? 1 : 0.45,
                    }}
                  />
                ))}
              </div>
            </div>
            <div className="mt-1 grid grid-cols-4 text-center text-[9.5px] text-[#94a3b8]">
              {riskConfig.segments.map(segment => (
                <span key={segment.key}>{segment.description}</span>
              ))}
            </div>
          </div>
        </div>
      </article>
    </section>
  );
};

export default SummaryCards;
